import React, { useEffect, useState } from "react";

//import components
import DropdownSelect from "../../../assets/reusableComponents/dropDownSelect";
import { ToastContainer, toast, Zoom, Bounce } from "react-toastify";
import customToasts from "../../../assets/reusableComponents/myToasts";

//import scss
import "./WaterInvoices.scss";
import "../../../assets/reusableComponents/InvoiceEditHighlight.scss";

//import icons
import { IoMdAdd } from "react-icons/io";
import { MdEdit } from "react-icons/md";
import { LiaSaveSolid } from "react-icons/lia";
import { TiCancel } from "react-icons/ti";
import { FaArrowCircleRight } from "react-icons/fa";

//redux imports
import { useSelector, useDispatch } from "react-redux";
import {
  listWaterInvoices,
  createInvoice,
  getAllInvoicesForLandlord,
} from "../../../../redux/slices/sliceActions";

//import resize function
import { vw, vh } from "../../../assets/reusableComponents/resizeFunction";
import ConfirmationModal from "./ConfirmationModal";

const WaterInvoices = ({ initialFilters, onNavigateToPage }) => {
  // redux methods
  const dispatch = useDispatch();
  const { tenants, properties, waterinvoices } = useSelector(
    (state) => state.landlordslice
  );

  // apartment  variables
  const [apartmentChoices, setApartmentChoices] = useState([]);
  const [selectedApartment, setSelectedApartment] =
    useState(initialFilters?.apartment ?? "Select Apartment");
  useEffect(() => {
    if (Object.keys(properties).length > 0) {
      setApartmentChoices([...properties.map((property) => property.name)]);
    }
  }, [properties]);

  const selectedApartmentObject = properties.find(
    (property) => property.name === selectedApartment
  );

  //month variables
  const [selectedMonth, setSelectedMonth] = useState(initialFilters?.month ?? "Select Month");
  const monthChoices = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  //year variables
  const [selectedYear, setSelectedYear] = useState(initialFilters?.year ?? "Select Year");
  const currentYear = new Date().getFullYear();
  const yearChoices = Array.from({ length: 21 }, (_, i) => currentYear - i);
  const [noSelectionsMade, setNoSelectionsMade] = useState(true);
  const [isInvoicesFetched, setIsInvoicesFetched] = useState(false);
  //function to check selection of the three
  useEffect(() => {
    if (
      selectedApartment === "Select Apartment" ||
      selectedMonth === "Select Month" ||
      selectedYear === "Select Year"
    ) {
      return;
    }

    // Reset the fetch flag before making new request
    setInvoices([]);
    setShowTable(false);
    setIsInvoicesFetched(false);

    const selectedApartmentID = properties.find(
      (property) => property.name == selectedApartment
    )?._id;

    if (selectedApartmentID) {
      const monthNumber = monthChoices.indexOf(selectedMonth) + 1;

      dispatch(
        listWaterInvoices({
          property_id: selectedApartmentID,
          month: monthNumber,
          year: selectedYear,
        })
      )
        .unwrap()
        .then((data) => {
          setIsInvoicesFetched(true);
        })
        .catch(() => {
          setIsInvoicesFetched(true); // Still true so button doesn't spin forever
          customToasts.error("Failed to load water invoices");
        });
    }
    setNoSelectionsMade(false);
  }, [selectedApartment, selectedMonth, selectedYear]);

  // apartment variables
  const [isEditingAll, setIsEditingAll] = useState(false);

  //invoice variables
  const [invoices, setInvoices] = useState([]);
  const [ignoredReadings, setIgnoredReadings] = useState([]); // NEW: state for ignored (highlighted) already-generated invoice meter readings

  // modal state
  const [showModal, setShowModal] = useState(false);
  const [showOverwriteModal, setShowOverwriteModal] = useState(false); // NEW: modal state for confirming overwrite when editing an ignored row
  const [pendingIgnoredEditId, setPendingIgnoredEditId] = useState(null); // NEW: state to track ignored invoice meter readings

  useEffect(() => {
    console.log("water invoices", waterinvoices);
    if (waterinvoices?.waterInvoices) {
      setInvoices(waterinvoices?.waterInvoices);
    } else {
      setInvoices([]);
    }

    // Populate ignored meter readings for VIEW mode only when property was created in the same month.
    // For existing properties, ignored readings are only shown when the user clicks "Create New"
    // (handled in handleCreateNewInvoice which reads from waterinvoices directly).
    if (waterinvoices?.ignoredInvoiceMeterReadings && waterinvoices?.isPropertyCreatedInSameMonth) {
      const preparedIgnored = waterinvoices.ignoredInvoiceMeterReadings.map(
        (ir) => ({
          // create a consistent shape for UI usage
          _id: ir.house?._id, // use house id as unique key
          house: ir.house || {},
          meter_reading: ir.meter_reading || {},
          waterAmount: ir.water, // amount from response
          isEdited: ir.isEdited,
          currentReading:
            ir.meter_reading?.current_reading ?? "",
          previous_reading:
            ir.meter_reading?.previous_reading ??
            ir.house?.initial_meter_reading ??
            null,
          isEditing: false,
        })
      );
      setIgnoredReadings(preparedIgnored);
    } else {
      setIgnoredReadings([]);
    }
  }, [isInvoicesFetched, waterinvoices]);

  //handle create new invoice
  const [showTable, setShowTable] = useState(false);
  const [readingsFetched, setReadingsFetched] = useState([]);
  
  // NEW: State for loading when clicking Create New
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const handleCreateNewInvoice = () => {
    if (
      selectedApartment === "Select Apartment" ||
      selectedMonth === "Select Month" ||
      selectedYear === "Select Year"
    ) {
      customToasts.error("Please fill in all fields");
      return;
    }

    const invoicesData = waterinvoices?.waterInvoices;
    if (invoicesData && invoicesData.length > 0) {
       customToasts.error("Invoices for this month have already been created");
       return;
    }

    if (!selectedApartmentObject?.houses || selectedApartmentObject.houses.length === 0) {
      customToasts.error("You can't create an invoice if there are no houses in the property");
      return;
    }

    setIsCreatingNew(true);
    setTimeout(() => {
      setShowTable(true);
      if (waterinvoices) {
        const preparedReadings = waterinvoices?.meterReadings
        ?.filter((reading) => reading.house?.status !== "vacant")
        .map((reading) => ({
          ...reading,
          previous_reading: reading.current_reading,
          currentReading: "", // Each reading gets its own independent currentReading
          isEditing: false,
          _id: reading._id, // Ensure each reading has a unique identifier
        }));
      console.log("preparedReadings", preparedReadings);

      const sortMeterReadings = (readings) => {
        if (!readings || !Array.isArray(readings)) {
          return [];
        }

        // Sort meter readings by house number
        const sortedReadings = [...readings].sort((a, b) => {
          const houseNumberA = a.house?.house_no || "";
          const houseNumberB = b.house?.house_no || "";

          // Extract numeric part from house numbers
          const numA = parseInt(houseNumberA.replace(/^\D+/g, ""), 10) || 0;
          const numB = parseInt(houseNumberB.replace(/^\D+/g, ""), 10) || 0;

          // Extract letter parts
          const lettersA = houseNumberA.replace(/\d/g, "").toLowerCase();
          const lettersB = houseNumberB.replace(/\d/g, "").toLowerCase();

          // If both are pure numbers, sort numerically
          if (lettersA === "" && lettersB === "") {
            return numA - numB;
          }

          // If one is pure number, place it first
          if (lettersA === "") return -1;
          if (lettersB === "") return 1;

          // If both are alphanumeric, sort by numeric part first, then by letters
          if (numA !== numB) return numA - numB;
          return lettersA.localeCompare(lettersB);
        });

        return sortedReadings;
      };
      setReadingsFetched(sortMeterReadings(preparedReadings));

      // NEW: also prepare ignored readings for display (prefilled)
      if (waterinvoices?.ignoredInvoiceMeterReadings) {
        const preparedIgnored = waterinvoices.ignoredInvoiceMeterReadings
          .filter((ir) => ir.house?.status !== "vacant")
          .map((ir) => ({
            _id: ir.house?._id,
            house: ir.house,
            // If API gave meter_reading object, use those readings
            previous_reading:
              ir.meter_reading?.previous_reading ??
              ir.house?.initial_meter_reading ??
              null,
            currentReading:
              ir.meter_reading?.current_reading !== undefined
                ? ir.meter_reading.current_reading
                : ir.house?.initial_meter_reading || "",
            meter_reading: ir.meter_reading || {},
            waterAmount: ir.water ?? null, // display amount from response
            isEdited: ir.isEdited,
            isEditing: false,
          }));
        const sortedIgnored = sortMeterReadings(preparedIgnored);
        setIgnoredReadings(sortedIgnored);
      } else {
        setIgnoredReadings([]);
      }
      setIsCreatingNew(false);
    }
    }, 800);
  };

  //edit functions
  const handleEditAll = async () => {
    if (!isEditingAll) {
      setIsEditingAll(true);
      // Enter edit mode for all items
      if (invoices.length > 0) {
        setInvoices((prevInvoices) =>
          prevInvoices.map((invoice) => ({
            ...invoice,
            isEditing: true,
          }))
        );
      }
      if (readingsFetched.length > 0) {
        setReadingsFetched((prevReadings) =>
          prevReadings.map((reading) => ({
            ...reading,
            isEditing: true,
          }))
        );
      }
    } else {
      // Save all changes before exiting edit mode
      await saveReadings();

      // Exit edit mode
      if (invoices.length > 0) {
        setInvoices((prevInvoices) =>
          prevInvoices.map((invoice) => ({
            ...invoice,
            isEditing: false,
          }))
        );
      }
      if (readingsFetched.length > 0) {
        setReadingsFetched((prevReadings) =>
          prevReadings.map((reading) => ({
            ...reading,
            isEditing: false,
          }))
        );
      }
    }
    setIsEditingAll(!isEditingAll);
  };

  const handleEditClick = (id) => {
    setReadingsFetched(
      readingsFetched.map((reading) =>
        reading._id === id
          ? { ...reading, isEditing: !reading.isEditing }
          : reading
      )
    );
  };

  // NEW: when clicking edit on an ignored (highlighted) row, ask for confirmation first
  const handleIgnoredEditClick = (id) => {
    // open overwrite confirmation modal
    setPendingIgnoredEditId(id);
    setShowOverwriteModal(true);
  };

  // NEW: called when user confirms overwrite for ignored reading
  const confirmIgnoredEdit = () => {
    if (!pendingIgnoredEditId) {
      setShowOverwriteModal(false);
      return;
    }
    setIgnoredReadings((prev) =>
      prev.map((r) =>
        r._id === pendingIgnoredEditId ? { ...r, isEditing: true } : r
      )
    );
    setPendingIgnoredEditId(null);
    setShowOverwriteModal(false);
  };

  const handleInputChange = (id, value) => {
    if (!id) {
      customToasts.error("Failed to save readings; no id");
      return;
    }

    // Update normal fetched readings (match by house._id or _id)
    const updatedNormal = readingsFetched.map((reading) =>
      (reading?.house?._id === id || reading._id === id)
        ? {
            ...reading,
            currentReading: value,
            current_reading: value,
          }
        : reading
    );
    setReadingsFetched(updatedNormal);

    // Update ignored readings too (match by house._id)
    const updatedIgnored = ignoredReadings.map((reading) =>
      reading?.house?._id === id || reading._id === id
        ? {
            ...reading,
            currentReading: value,
            meter_reading: {
              ...reading.meter_reading,
              current_reading: value,
            },
          }
        : reading
    );
    setIgnoredReadings(updatedIgnored);
  };

  // Modify the saveReadings function to properly format data for the backend
  const saveReadings = async () => {
    try {
      // Update the local state to reflect the saved values for normal readings
      setReadingsFetched(
        readingsFetched.map((reading) => ({
          ...reading,
          current_reading: reading.currentReading,
          isEditing: false,
        }))
      );

      // Also update ignored readings if any were edited: set meter_reading.current_reading and isEditing false
      setIgnoredReadings((prev) =>
        prev.map((r) => ({
          ...r,
          meter_reading: {
            ...r.meter_reading,
            current_reading:
              r.currentReading !== undefined && r.currentReading !== ""
                ? r.currentReading
                : r.meter_reading?.current_reading || r.currentReading,
          },
          isEditing: false,
        }))
      );

      customToasts.success("Readings saved successfully");
    } catch (error) {
      customToasts.error("Failed to save readings");
      console.error("Error saving readings:", error);
    }
  };

  const createInvoiceFunction = () => {
    // Check if any reading (normal or ignored) has empty currentReading
    const emptyNormal = readingsFetched.filter(
      (reading) => !reading.currentReading || reading.currentReading === ""
    );
    const emptyIgnored = ignoredReadings.filter(
      (reading) => !reading.currentReading || reading.currentReading === ""
    );

    if (emptyNormal.length > 0 || emptyIgnored.length > 0) {
      customToasts.error("Please fill in all current readings");
      return;
    }

    // Combine normal and ignored readings into single payload
    const normalPayload = readingsFetched.map((reading) => ({
      id: reading?.house?._id || reading._id,
      current_recording: reading.currentReading,
      house_id: reading.house?._id,
      property_id: reading.house?.property?._id,
      month: monthChoices.indexOf(selectedMonth) + 1,
      year: selectedYear,
    }));

    const ignoredPayload = ignoredReadings.map((reading) => ({
      id: reading?.house?._id,
      current_recording:
        reading.currentReading !== undefined && reading.currentReading !== ""
          ? reading.currentReading
          : reading.meter_reading?.current_reading || reading.currentReading,
      house_id: reading.house?._id,
      property_id: reading.house?.property?._id,
      month: reading.meter_reading?.month || monthChoices.indexOf(selectedMonth) + 1,
      year: reading.meter_reading?.year || selectedYear,
    }));

    const updatedReadings = [...normalPayload, ...ignoredPayload];

    console.log("Data to be sent to backend:", updatedReadings);
    if (updatedReadings.length > 0) {
      customToasts.promise(
        dispatch(
          createInvoice({
            propertyId: updatedReadings[0].property_id,
            updatedReadings,
          })
        ).unwrap().then(async (res) => {
          if (res?.status !== "success") {
            throw new Error(res?.message || "Failed to create invoice");
          }

          // Leave creation mode as soon as the invoices have been saved.
          setShowTable(false);
          setIsEditingAll(false);
          setReadingsFetched([]);
          setShowOverwriteModal(false);
          setPendingIgnoredEditId(null);
          setIsInvoicesFetched(false);

          try {
            await dispatch(
              listWaterInvoices({
                property_id: updatedReadings[0].property_id,
                month: monthChoices.indexOf(selectedMonth) + 1,
                year: selectedYear,
              })
            ).unwrap();
          } catch {
            customToasts.error("Invoices created, but failed to load water invoices. Please refresh the page.");
          } finally {
            setIsInvoicesFetched(true);
          }
          dispatch(getAllInvoicesForLandlord());
          return res;
        }),
        {
          pending: "Creating invoice...",
          success: "Invoice created successfully",
          error: "Failed to create invoice",
        }
      ).catch(() => {});
    }
  };

  const handleCancelInvoice = () => {
    // Reset the reading data back to original state
    if (waterinvoices) {
      const preparedReadings = waterinvoices?.meterReadings.map((reading) => ({
        ...reading,
        previous_reading: reading.current_reading,
        currentReading: "", // Empty string for new input
        isEditing: false,
      }));
      setReadingsFetched(preparedReadings);
    }

    // Hide the table
    setShowTable(false);

    // Reset edit mode if it was active
    setIsEditingAll(false);

    customToasts.info("Invoice creation cancelled");
  };

  const [animateButton, setAnimateButton] = useState(false);

  useEffect(() => {
    // Set animateButton to true when there are no invoices (regular or ignored) and selections have been made
    setAnimateButton(
      isInvoicesFetched && 
      !noSelectionsMade && 
      invoices.length === 0 && 
      ignoredReadings.length === 0 && 
      !showTable &&
      (selectedApartmentObject?.houses && selectedApartmentObject.houses.length > 0)
    );
  }, [isInvoicesFetched, noSelectionsMade, invoices.length, ignoredReadings.length, showTable, selectedApartmentObject]);

  //navigate to rent invoices
  const handleNavigateToRentInvoices = () => {
    // If you're using this within MidSectionInvoicesPc, you'll need to pass this function as a prop
    if (typeof onNavigateToPage === "function") {
      onNavigateToPage("Rent", { apartment: selectedApartment, month: selectedMonth, year: selectedYear });
    }
  };

  return (
    <div className="water-invoices-page">
      <div className="title-section">
        <div className="title-and-description">
          <div className="title">Water Invoices</div>
          <div className="description">View Water Invoice History</div>
        </div>
        <div
          className={`create-expense-button ${
            animateButton ? "animate-button" : ""
          }`}
          onClick={isCreatingNew || !isInvoicesFetched ? undefined : handleCreateNewInvoice}
          style={{
            opacity: waterinvoices?.waterInvoices?.length > 0 || isCreatingNew || !isInvoicesFetched || (!selectedApartmentObject?.houses || selectedApartmentObject.houses.length === 0) ? 0.5 : 1,
            cursor: waterinvoices?.waterInvoices?.length > 0 || isCreatingNew || !isInvoicesFetched || (!selectedApartmentObject?.houses || selectedApartmentObject.houses.length === 0) ? 'not-allowed' : 'pointer'
          }}
        >
          {isCreatingNew ? (
            <div className="spinner" style={{ width: "20px", height: "20px", border: "2px solid #fff", borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
          ) : (
            <IoMdAdd />
          )}
          Create New
        </div>
      </div>

      <div className="long-rectangle">
        <div className="overlay">
          <div className="left-rectangle">
            <div className="title">All Water Invoices</div>
            <div className="bottom-section">
              <div className="filters-section">
                <div className="box">
                  <div className="box-title">Apartment Name</div>
                  <div className="select">
                    <DropdownSelect
                      choices={apartmentChoices}
                      defaultChoice={selectedApartment}
                      onSelect={setSelectedApartment}
                      backgroundColor={"#ffffff"}
                      textColor={"#192a3a"}
                      border={"none"}
                    />
                  </div>
                </div>
                <div className="box">
                  <div className="box-title">Month </div>
                  <div className="select">
                    <DropdownSelect
                      choices={monthChoices}
                      defaultChoice={selectedMonth}
                      onSelect={setSelectedMonth}
                      backgroundColor={"#ffffff"}
                      textColor={"#192a3a"}
                      border={"none"}
                    />
                  </div>
                </div>
                <div className="box">
                  <div className="box-title">Select Year</div>
                  <div className="select">
                    <DropdownSelect
                      choices={yearChoices}
                      defaultChoice={selectedYear}
                      onSelect={setSelectedYear}
                      backgroundColor={"#ffffff"}
                      textColor={"#192a3a"}
                      border={"none"}
                    />
                  </div>
                </div>
              </div>
              <div className="description">
                Apply filters to view specific invoices
              </div>
            </div>
          </div>
        </div>
      </div>

      {noSelectionsMade ? (
        <div className="no-selections-made">
          <div className="text">
            Select the relevant fields to view invoices
          </div>
        </div>
      ) : !isInvoicesFetched ? (
        <div className="no-selections-made">
          <div className="text" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <div className="spinner" style={{ width: "40px", height: "40px", border: "4px solid #00B1A9", borderTop: "4px solid transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
            Fetching water invoices...
          </div>
        </div>
      ) : (
        <>
          {!showTable && (invoices.length > 0 || ignoredReadings.length > 0) ? (
            <div className="invoice-grid">
              <div
                className="title-box"
                style={{ margin: `${vh(20)} 0px 0px 0px` }}
              >
                <div className="title">House Number</div>
                <div className="title">Unit Cost</div>
                <div className="title">Previous Reading</div>
                <div className="title">Current Reading</div>
                <div className="title">Total</div>
              </div>
              <div className="invoice-box">
                {/* existing generated invoices (editable as before) */}
                {invoices.map((invoice) => (
                  <div
                    key={invoice._id || invoice.id}
                    className={`invoice-row ${invoice.isEdited?.water === true ? "invoice-row-edited" : ""}`}
                    title={invoice.isEdited?.water === true ? "Water amount manually changed" : undefined}
                  >
                    <div className="invoice-data">{invoice.house?.house_no}</div>
                    <div className="invoice-data">
                      {invoice.property?.water_charge}
                    </div>
                    <div className="invoice-data">
                      {invoice?.meter_reading?.previous_reading}
                    </div>
                    <div className="invoice-data">
                      {invoice.isEditing ? (
                        <input
                          type="text"
                          value={invoice.currentReading}
                          onChange={(e) =>
                            handleInputChange(invoice.id || invoice._id, e.target.value)
                          }
                          className="editable-input"
                        />
                      ) : (
                        invoice?.meter_reading?.current_reading
                      )}
                    </div>
                    <div className="invoice-data">
                      {invoice.water ?? calculateTotal(
                        invoice.meter_reading?.current_reading,
                        invoice.meter_reading?.previous_reading,
                        invoice.property?.water_charge
                      )}
                    </div>
                  </div>
                ))}

                {/* show ignored (highlighted) readings as non-editable rows */}
                {ignoredReadings.length > 0 &&
                  ignoredReadings.map((reading) => (
                    <div
                      key={`ignored-${reading._id}`}
                      className={`invoice-row ignored-row ${reading.isEdited?.water === true ? "invoice-row-edited" : ""}`}
                      title={reading.isEdited?.water === true ? "Water amount manually changed" : undefined}
                      style={{ cursor: "default" }}
                    >
                      <div className="invoice-data">
                        {reading.house?.house_no}
                      </div>
                      <div className="invoice-data">
                        {reading?.house?.property?.water_charge}
                      </div>
                      <div className="invoice-data">
                        {reading.previous_reading ??
                          reading.meter_reading?.previous_reading ??
                          "not set"}
                      </div>
                      <div className="invoice-data">
                        {reading.meter_reading?.current_reading ??
                          reading.currentReading ??
                          "not set"}
                      </div>
                      <div className="invoice-data">
                        {/* Use the amount from API where provided, otherwise fall back to calculation */}
                        {reading.water !== undefined
                          ? reading.water
                          : reading.waterAmount !== undefined
                          ? reading.waterAmount
                          : calculateTotal(
                              reading.meter_reading?.current_reading || reading.currentReading || 0,
                              reading.previous_reading || reading.meter_reading?.previous_reading || 0,
                              reading?.house?.property?.water_charge || 0
                            )}
                      </div>
                    </div>
                  ))}
              </div>
              <div
                className="navigate-to-rent-invoices-button"
                onClick={handleNavigateToRentInvoices}
              >
                <span>Rent Invoices</span>
                <FaArrowCircleRight />
              </div>
            </div>
          ) : (
            <>
              {showTable ? (
                <>
                  <div className="invoice-grid">
                    {/* NEW: Informational banner shown when there are ignored (highlighted) rows */}
                    {ignoredReadings.length > 0 && (
                      <div
                        className="ignored-banner"
                        style={{
                          width: "97%",
                          margin: `${vh(10)} 0px`,
                          padding: `${vh(12)} ${vw(20)}`,
                          borderRadius: `${vw(6)}`,
                          background: "#FFF7E6",
                          color: "#5a3d00",
                        }}
                      >
                        The highlighted invoices represent tenants who joined in
                        the selected month and already have water invoices
                        generated. Modifying any highlighted invoice will
                        overwrite their already-generated invoice. You will be
                        prompted to confirm before editing.
                      </div>
                    )}
                    <div
                      className="six-rows"
                      style={{ margin: `${vh(20)} 0px 0px 0px` }}
                    >
                      <div className="title">House Number</div>
                      <div className="title">Unit Cost</div>
                      <div className="title">Previous Reading</div>
                      <div className="title">Current Reading</div>
                      <div className="title">Total / Amount</div>
                      <div className="title">
                        {isEditingAll ? (
                          <div
                            onClick={handleEditAll}
                            className="actions-button"
                          >
                            <div className="text">Save All</div>
                            <LiaSaveSolid />
                          </div>
                        ) : (
                          <div
                            onClick={handleEditAll}
                            className="actions-button"
                          >
                            <div className="text">Edit All</div>
                            <MdEdit />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="invoice-box">
                      {/* NEW: Render ignored (highlighted) readings first */}
                      {ignoredReadings.map((reading) => (
                        <div
                          key={`ignored-${reading._id}`}
                          className={`six-rows-invoice ignored-row ${reading.isEdited?.water === true ? "invoice-row-edited" : ""}`}
                          title={reading.isEdited?.water === true ? "Water amount manually changed" : undefined}
                        >
                          <div className="invoice-data">
                            {reading.house?.house_no}
                          </div>
                          <div className="invoice-data">
                            {reading?.house?.property?.water_charge}
                          </div>
                          <div className="invoice-data">
                            {reading.previous_reading ?? "not set"}
                          </div>
                          <div className="invoice-data">
                            {reading.isEditing ? (
                              <input
                                type="number"
                                value={reading.currentReading || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    reading?.house?._id,
                                    e.target.value
                                  )
                                }
                                className="editable-input"
                                placeholder="Enter new reading"
                              />
                            ) : (
                              reading.currentReading ?? "not set"
                            )}
                          </div>

                          {/* NEW: show amount from response instead of calculated total */}
                          <div className="invoice-data">
                            {reading.waterAmount !== null &&
                            reading.waterAmount !== undefined
                              ? reading.waterAmount
                              : calculateTotal(
                                  reading.currentReading || 0,
                                  reading.previous_reading || 0,
                                  reading?.house?.property?.water_charge || 0
                                )}
                          </div>

                          <div
                            className="reading-data"
                            style={{ color: "#B39359", cursor: "pointer" }}
                            onClick={() => handleIgnoredEditClick(reading._id)}
                            title="Edit (will overwrite generated invoice)"
                          >
                            <MdEdit />
                          </div>
                        </div>
                      ))}

                      {/* Render normal/new readings */}
                      {readingsFetched.map((reading) => (
                        <div key={reading._id} className="six-rows-invoice">
                          <div className="invoice-data">
                            {reading.house?.house_no}
                          </div>
                          <div className="invoice-data">
                            {reading?.house?.property?.water_charge}
                          </div>
                          <div className="invoice-data">
                            {reading.previous_reading || "not set"}
                          </div>
                          <div className="invoice-data">
                            {reading.isEditing ? (
                              <input
                                type="number"
                                value={reading.currentReading || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    reading?.house?._id,
                                    e.target.value
                                  )
                                }
                                className="editable-input"
                                placeholder="Enter new reading"
                              />
                            ) : (
                              reading.currentReading || "not set"
                            )}
                          </div>
                          <div className="invoice-data">
                            {calculateTotal(
                              reading.currentReading || 0,
                              reading.previous_reading || 0,
                              reading?.house?.property?.water_charge || 0
                            )}
                          </div>
                          <div
                            className="reading-data"
                            style={{ color: "#B39359" }}
                            onClick={() => handleEditClick(reading._id)}
                          >
                            <MdEdit />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="buttons-container">
                    <div
                      className="confirm-button"
                      onClick={() => setShowModal(true)}
                    >
                      <div className="text">Confirm</div>
                      <LiaSaveSolid />
                    </div>
                    <div
                      className="cancel-button"
                      onClick={handleCancelInvoice}
                    >
                      <div className="text">Cancel</div>
                      <TiCancel />
                    </div>
                  </div>
                </>
              ) : (!selectedApartmentObject?.houses || selectedApartmentObject.houses.length === 0) ? (
                <div className="no-selections-made">
                  <div className="text">
                    There are no houses in {selectedApartment} yet. <br />
                    Please add houses to this property first.
                  </div>
                </div>
              ) : (
                <div className="no-selections-made">
                  <div className="text">
                    No invoices found for {selectedApartment} in {selectedMonth}{" "}
                    {selectedYear} <br />
                    Click create new to add new invoices
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* EXISTING: create-invoice confirmation modal */}
      {showModal && (
        <ConfirmationModal
          setShowModal={setShowModal}
          title="Create Invoice Confirmation"
          message="Are you sure you want to create this invoice? This action cannot be undone."
          confirmText="Create Invoice"
          cancelText="Cancel"
          onConfirm={createInvoiceFunction}
          onCancel={handleCancelInvoice}
        />
      )}

      {/* NEW: overwrite confirmation modal for ignored rows */}
      {showOverwriteModal && (
        <ConfirmationModal
          setShowModal={setShowOverwriteModal}
          title="Overwrite Generated Invoice?"
          message="This invoice was already generated for the tenant. Editing this reading will overwrite the generated invoice. Do you want to proceed?"
          confirmText="Proceed & Edit"
          cancelText="Cancel"
          onConfirm={confirmIgnoredEdit}
          onCancel={() => {
            setShowOverwriteModal(false);
            setPendingIgnoredEditId(null);
          }}
        />
      )}

      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </div>
  );
};

const calculateTotal = (currentReading, previousReading, unitCost) => {
  return (currentReading - previousReading) * unitCost;
};

export default WaterInvoices;
